/*
  <mms-restock-form> — the sold-out "Email me when it's back" form
  (snippets/mms-restock-form.liquid), replacing the Alert Me app. Loaded on every page by
  snippets/mms-wishlist-head.liquid while mms_wishlist_enabled is on, so it also works in
  quick shop content inserted after load.

  Visibility: shown only while the selected variant is sold out. The element follows the
  product form of its enclosing .product-section: variant:change on document carries the
  picked variant (used when it's one of this product's), and Impulse fires a bubbling
  change on the master [data-product-select] after it. The master select has no value for
  sold-out variants, so it only helps for available ones. Availability per variant comes
  from the JSON in [data-restock-availability].

  Submit: POST /apps/mms/signup (App Proxy, JSON) { variantId, email? (guests), hp? }.
  Signed-in customers send no email; the server uses their account email.
    200 { status: 'waiting' }  -> "Thanks! …" replaces the form for that variant. Kept in
                                  sessionStorage mms_restock_done (variant IDs), so switching
                                  variants and back still shows it this session.
    200 { status: 'in_stock' } -> "Good news: it's in stock now. …"
    403 / 404                  -> the form hides for this product (switched off, not live).
    anything else              -> "Something went wrong. …", the form stays.
  The button is disabled while a request is out. The status line is aria-live.
*/
(function () {
  'use strict';

  if (customElements.get('mms-restock-form')) {
    return;
  }

  var ENDPOINT = '/apps/mms/signup';
  var DONE_KEY = 'mms_restock_done';
  var TIMEOUT = 15000;
  var MESSAGES = {
    waiting: "Thanks! We'll email you when it's back.",
    in_stock: "Good news: it's in stock now. Refresh to add it to your cart.",
    error: 'Something went wrong. Please try again.'
  };

  function doneVariants() {
    try {
      var list = JSON.parse(window.sessionStorage.getItem(DONE_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function markDone(variantId) {
    try {
      var list = doneVariants();
      if (list.indexOf(variantId) === -1) list.push(variantId);
      window.sessionStorage.setItem(DONE_KEY, JSON.stringify(list.slice(-100)));
    } catch (e) { /* the confirmation just won't survive a variant switch */ }
  }

  function post(body) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, TIMEOUT) : null;
    return fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (timer) clearTimeout(timer);
      return response.json().catch(function () { return {}; }).then(function (data) {
        return { status: response.status, data: data || {} };
      });
    }, function () {
      if (timer) clearTimeout(timer);
      return { status: 0, data: {} };
    });
  }

  customElements.define(
    'mms-restock-form',
    class MMSRestockForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-restock-form]');
        this.button = this.querySelector('[data-restock-button]');
        this.status = this.querySelector('[data-restock-status]');
        if (!this.form) return;

        if (!this.availability) {
          try {
            var json = this.querySelector('[data-restock-availability]');
            this.availability = JSON.parse(json ? json.textContent : '{}') || {};
          } catch (e) {
            this.availability = {};
          }
        }
        this.variantId = this.variantId || this.dataset.variantId || '';
        this.results = this.results || {}; // variantId -> 'in_stock', this page load

        if (!this._onSubmit) {
          this._onSubmit = this.submit.bind(this);
          this._onFormChange = this.onFormChange.bind(this);
          this._onVariantChange = this.onVariantChange.bind(this);
        }
        this.form.addEventListener('submit', this._onSubmit);
        this.root = this.closest('.product-section') || null;
        if (this.root) {
          this.root.addEventListener('change', this._onFormChange);
          document.addEventListener('variant:change', this._onVariantChange);
        }
        // No initial sync: Liquid rendered the selected variant, and the master select
        // shows the first available one when the selected variant is sold out.
        this.update();
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this._onSubmit);
        if (this.root) this.root.removeEventListener('change', this._onFormChange);
        document.removeEventListener('variant:change', this._onVariantChange);
        this.root = null;
      }

      syncVariant() {
        if (!this.root) return;
        var select = this.root.querySelector('[data-product-select]') || this.root.querySelector('[name="id"]');
        if (select && select.value && select.value !== this.variantId) {
          this.variantId = select.value;
          this.dataset.variantId = select.value;
          this.update();
        }
      }

      onFormChange(evt) {
        var target = evt.target;
        if (target && target.matches && target.matches('[data-product-select], [name="id"]')) this.syncVariant();
      }

      onVariantChange(evt) {
        // The event's variant is the one picked, sold out or not (the master select has no
        // value for sold-out variants). It isn't scoped to a form, so only take a variant
        // of this product.
        var variant = evt.detail && evt.detail.variant;
        if (variant && Object.prototype.hasOwnProperty.call(this.availability, String(variant.id))) {
          if (String(variant.id) !== this.variantId) {
            this.variantId = String(variant.id);
            this.dataset.variantId = this.variantId;
            this.update();
          }
          return;
        }
        // Impulse fires variant:change before it updates the master select.
        setTimeout(this.syncVariant.bind(this), 0);
      }

      update() {
        if (this.switchedOff) {
          this.hidden = true;
          return;
        }
        var soldOut = this.availability[this.variantId] === false;
        this.hidden = !soldOut;
        if (!soldOut) return;

        var result = doneVariants().indexOf(this.variantId) !== -1 ? 'waiting' : this.results[this.variantId];
        this.form.hidden = !!result;
        this.status.textContent = result ? MESSAGES[result] : '';
        this.status.classList.remove('mms-restock__status--error');
      }

      submit(evt) {
        evt.preventDefault();
        if (this.busy) return;
        var variantId = this.variantId;
        if (!/^\d+$/.test(variantId)) return;

        var body = { variantId: variantId };
        var email = this.form.querySelector('input[name="email"]');
        if (email) {
          var value = email.value.trim();
          if (!value || !email.checkValidity()) {
            email.reportValidity();
            return;
          }
          body.email = value;
        }
        var hp = this.form.querySelector('input[name="hp"]');
        if (hp && hp.value) body.hp = hp.value;

        this.busy = true;
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        this.status.textContent = '';
        this.status.classList.remove('mms-restock__status--error');

        post(body).then(function (res) {
          this.busy = false;
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');

          if (res.status === 200 && (res.data.status === 'waiting' || res.data.status === 'in_stock')) {
            if (res.data.status === 'waiting') markDone(variantId);
            else this.results[variantId] = 'in_stock';
            if (variantId === this.variantId) this.update();
            return;
          }
          if (res.status === 403 || res.status === 404) {
            this.switchedOff = true;
            this.update();
            return;
          }
          this.status.textContent = MESSAGES.error;
          this.status.classList.add('mms-restock__status--error');
        }.bind(this));
      }
    }
  );
})();
